import type { GridProps } from "@chakra-ui/react";
import { Box, Grid, Flex, Text, Link, VStack, Skeleton, useColorMode, Divider, useColorModeValue } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import type { CustomLinksGroup } from "types/footerLinks";

import config from "configs/app";
import type { ResourceError } from "lib/api/resources";
import useApiQuery from "lib/api/useApiQuery";
import useFetch from "lib/hooks/useFetch";
import useIssueUrl from "lib/hooks/useIssueUrl";
import NetworkAddToWallet from "ui/shared/NetworkAddToWallet";

import FooterLinkItem from "./FooterLinkItem";
import IntTxsIndexingStatus from "./IntTxsIndexingStatus";
import getApiVersionUrl from "./utils/getApiVersionUrl";
import IconSvg from "ui/shared/IconSvg";
import useIsMobile from "lib/hooks/useIsMobile";
import useProvider from "lib/web3/useProvider";
import useToast from "lib/hooks/useToast";

const MAX_LINKS_COLUMNS = 4;

const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${config.UI.footer.frontendVersion}`;
const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${config.UI.footer.frontendCommit}`;

const Footer = () => {
  const { data: backendVersionData } = useApiQuery("config_backend_version", {
    queryOptions: {
      staleTime: Infinity,
    },
  });
  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const issueUrl = useIssueUrl(backendVersionData?.backend_version);
  const BLOCKSCOUT_LINKS = [
    {
      icon: "edit" as const,
      iconSize: "16px",
      text: "Submit an issue",
      url: issueUrl,
    },
    {
      icon: "social/canny" as const,
      iconSize: "20px",
      text: "Feature request",
      url: "https://blockscout.canny.io/feature-requests",
    },
    {
      icon: "social/git" as const,
      iconSize: "18px",
      text: "Donate",
      url: "https://github.com/blockscout/blockscout",
    },
    {
      icon: "social/git" as const,
      iconSize: "18px",
      text: "Github",
      url: "https://github.com/depaasecology/de-evm",
    },
    {
      icon: "social/twitter" as const,
      iconSize: "24px",
      text: "X (ex-Twitter)",
      url: "https://x.com/DEPaaS_Labs",
    },
    {
      icon: "social/discord" as const,
      iconSize: "24px",
      text: "Discord",
      url: "http://discord.gg/depaas",
    },
    {
      icon: "telegram" as const,
      iconSize: "20px",
      text: "Telegram",
      url: "https://t.me/+OQS6Nr45uTIyNWNl",
    },
    {
      icon: "medium" as const,
      iconSize: "20px",
      text: "Medium",
      url: "https://medium.com/@DEPaaS",
    },
  ];

  const frontendLink = (() => {
    if (config.UI.footer.frontendVersion) {
      return (
        <Link href={FRONT_VERSION_URL} target="_blank">
          {config.UI.footer.frontendVersion}
        </Link>
      );
    }

    if (config.UI.footer.frontendCommit) {
      return (
        <Link href={FRONT_COMMIT_URL} target="_blank">
          {config.UI.footer.frontendCommit}
        </Link>
      );
    }

    return null;
  })();

  const fetch = useFetch();
  const { colorMode } = useColorMode();
  const isMobile = useIsMobile();
  const { provider } = useProvider();
  const toast = useToast();
  const topColor = useColorModeValue("black", "white");

  const handleAddNetworkToWallet = () => {
    if (provider?.chainId === "0x31512") {
      return toast({
        position: "top-right",
        title: "Success",
        description: "Already added",
        status: "success",
        variant: "subtle",
        isClosable: true,
      });
    }
    provider
      ?.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: "0x31512",
            chainName: "DEP",
            iconUrls: ["https://docs.digitalecology.de/img/logo.svg"],
            rpcUrls: ["https://dechain19.depaas.de/", "https://dechain13.depaas.de"],
            nativeCurrency: {
              name: "DE",
              symbol: "DE",
              decimals: 18,
            },
            blockExplorerUrls: ["https://explorer.depaas.de"],
          },
        ],
      })
      .then(() =>
        toast({
          position: "top-right",
          title: "Success",
          status: "success",
          variant: "subtle",
          isClosable: true,
        })
      );
  };

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: ["footer-links"],
    queryFn: async () =>
      fetch(config.UI.footer.links || "", undefined, {
        resource: "footer-links",
      }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps["gridArea"]) => {
    return (
      <Flex gridArea={gridArea} flexWrap="wrap" columnGap={8} rowGap={6} mb={{ base: 5, lg: 10 }} _empty={{ display: "none" }}>
        {!config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus />}
        <NetworkAddToWallet />
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback(
    (gridArea?: GridProps["gridArea"]) => {
      return (
        <Box gridArea={gridArea}>
          {/* <Link fontSize="xs" href="https://www.blockscout.com">blockscout.com</Link> */}
          <Text mt={3} fontSize="xs">
            DEScan is a Block Explorer and Analytics Platform for DEP.
          </Text>
          <VStack spacing={1} mt={6} alignItems="start">
            {apiVersionUrl && (
              <Text fontSize="xs">
                Backend:{" "}
                <Link href={apiVersionUrl} target="_blank">
                  {backendVersionData?.backend_version}
                </Link>
              </Text>
            )}
            {/* { frontendLink && (
            <Text fontSize="xs">
              Frontend: { frontendLink }
            </Text>
          ) } */}
          </VStack>
        </Box>
      );
    },
    [apiVersionUrl, backendVersionData?.backend_version, frontendLink]
  );

  const containerProps: GridProps = {
    as: "footer",
    px: { base: 4, lg: 12 },
    py: { base: 4, lg: 9 },
    borderTop: "1px solid",
    borderColor: "divider",
    gridTemplateColumns: { base: "1fr", lg: "minmax(auto, 470px) 1fr" },
    columnGap: { lg: "32px", xl: "100px" },
  };

  if (config.UI.footer.links) {
    return (
      <Grid {...containerProps}>
        <div>
          {renderNetworkInfo()}
          {renderProjectInfo()}
        </div>

        <Grid
          gap={{
            base: 6,
            lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8,
            xl: 12,
          }}
          gridTemplateColumns={{
            base: "repeat(auto-fill, 160px)",
            lg: `repeat(${colNum}, 135px)`,
            xl: `repeat(${colNum}, 160px)`,
          }}
          justifyContent={{ lg: "flex-end" }}
          mt={{ base: 8, lg: 0 }}
        >
          {[{ title: "Blockscout", links: BLOCKSCOUT_LINKS }, ...(linksData || [])].slice(0, colNum).map((linkGroup) => (
            <Box key={linkGroup.title}>
              <Skeleton fontWeight={500} mb={3} display="inline-block" isLoaded={!isPlaceholderData}>
                {linkGroup.title}
              </Skeleton>
              <VStack spacing={1} alignItems="start">
                {linkGroup.links.map((link) => (
                  <FooterLinkItem {...link} key={link.text} isLoading={isPlaceholderData} />
                ))}
              </VStack>
            </Box>
          ))}
        </Grid>
      </Grid>
    );
  }
  const grid = (
    <Grid
      {...containerProps}
      gridTemplateAreas={{
        lg: `
    "network links-top"
    "info links-bottom"
  `,
      }}
    >
      {/* { renderNetworkInfo({ lg: 'network' }) } */}
      {renderProjectInfo({ lg: "info" })}

      <Grid
        gridArea={{ lg: "links-bottom" }}
        gap={1}
        gridTemplateColumns={{
          base: "repeat(auto-fill, 160px)",
          lg: "repeat(3, 160px)",
          xl: "repeat(4, 160px)",
        }}
        gridTemplateRows={{
          base: "auto",
          lg: "repeat(3, auto)",
          xl: "repeat(2, auto)",
        }}
        gridAutoFlow={{ base: "row", lg: "column" }}
        alignContent="start"
        justifyContent={{ lg: "flex-end" }}
        mt={{ base: 8, lg: 0 }}
      >
        {BLOCKSCOUT_LINKS.map((link) => (
          <FooterLinkItem {...link} key={link.text} />
        ))}
      </Grid>
    </Grid>
  );

  if (isMobile) return grid;
  return (
    <Box w={{ lg: "1440px" }} ml="auto" mr="auto" mt={10}>
      <Text
        fontSize="xs"
        as="button"
        display="flex"
        alignItems="center"
        ml="auto"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <IconSvg name="arrows_roof" w="13px" h="13px" color={topColor} mr="1" />
        Back to Top
      </Text>
      <Divider mt={2} />
      <Flex mt={10}>
        <Box>
          <IconSvg name={colorMode === "dark" ? "depaas_logo" : "depaas_logo_footer"} w="161px" h="46px" />
          {renderProjectInfo({ lg: "info" })}
          <Flex
            as="button"
            border="none"
            rounded="8px"
            w={{ lg: "153px" }}
            h={{ lg: "40px" }}
            bg="gray.100"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: "gray.200" }}
            onClick={handleAddNetworkToWallet}
          >
            <IconSvg name="wallets/metamask" w="26px" h="26px" />
            <Text fontSize="xs" color="#121212" ml="8px">
              Add DEP Network
            </Text>
          </Flex>
        </Box>

        <Box ml="auto">
          <Text fontSize="md" fontWeight={800}>
            Products & Services
          </Text>
          {BLOCKSCOUT_LINKS.slice(0, 3).map((link) => (
            <FooterLinkItem {...link} key={link.text} />
          ))}
        </Box>

        <Box ml="186px">
          <Text fontSize="md" fontWeight={800}>
            Community
          </Text>
          {BLOCKSCOUT_LINKS.slice(3).map((link) => (
            <FooterLinkItem {...link} key={link.text} />
          ))}
        </Box>
      </Flex>
      <Divider mt={2} />
      <Text fontSize="xs" color="GrayText" ml="auto" mr="auto" my={2} w="fit-content">
        Copyright © 2004 - {new Date().getFullYear()} LLC. All rights reserved
      </Text>
    </Box>
  );
};

export default React.memo(Footer);
